import { useEffect, useState } from "react";
import { useLocation, useParams } from "wouter";
import { useForm } from "react-hook-form";
import api from "@/lib/api";
import { ArrowLeft } from "lucide-react";
import { Link } from "wouter";

interface FormData {
  name: string;
  company: string;
  text: string;
  rating: number;
  sortOrder: number;
}

export default function TestimonialForm() {
  const params = useParams<{ id: string }>();
  const isEdit = params.id && params.id !== "new";
  const [, navigate] = useLocation();
  const [saving, setSaving] = useState(false);

  const { register, handleSubmit, reset } = useForm<FormData>({
    defaultValues: { name: "", company: "", text: "", rating: 5, sortOrder: 0 },
  });

  useEffect(() => {
    if (isEdit) {
      api.get(`/testimonials`).then(({ data }) => {
        const t = data.find((t: any) => t.id === parseInt(params.id!));
        if (t) reset(t);
      });
    }
  }, [isEdit, params.id, reset]);

  const onSubmit = async (data: FormData) => {
    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/testimonials/${params.id}`, data);
      } else {
        await api.post("/testimonials", data);
      }
      navigate("/admin/testimonials");
    } catch (err: any) {
      alert(err.response?.data?.error || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <Link href="/admin/testimonials" className="text-white/40 hover:text-white transition-colors">
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <h1 className="text-2xl font-bold text-white font-[Rubik]">
          {isEdit ? "Edit Testimonial" : "New Testimonial"}
        </h1>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="max-w-xl space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm text-white/60 mb-1.5">Name</label>
            <input
              {...register("name")}
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:border-[#66CDAA]/50 outline-none"
              dir="rtl"
              required
            />
          </div>
          <div>
            <label className="block text-sm text-white/60 mb-1.5">Company</label>
            <input
              {...register("company")}
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:border-[#66CDAA]/50 outline-none"
              dir="rtl"
              required
            />
          </div>
        </div>

        <div>
          <label className="block text-sm text-white/60 mb-1.5">Text</label>
          <textarea
            {...register("text")}
            rows={4}
            className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:border-[#66CDAA]/50 outline-none resize-none"
            dir="rtl"
            required
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm text-white/60 mb-1.5">Rating (1-5)</label>
            <input
              type="number"
              {...register("rating", { valueAsNumber: true })}
              min={1}
              max={5}
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:border-[#66CDAA]/50 outline-none"
              required
            />
          </div>
          <div>
            <label className="block text-sm text-white/60 mb-1.5">Sort order</label>
            <input
              type="number"
              {...register("sortOrder", { valueAsNumber: true })}
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:border-[#66CDAA]/50 outline-none"
            />
          </div>
        </div>

        <div className="flex gap-3">
          <button
            type="submit"
            disabled={saving}
            className="bg-[#66CDAA] hover:bg-[#57b899] text-[#0F172A] font-bold px-6 py-3 rounded-xl transition-colors disabled:opacity-50"
          >
            {saving ? "Saving..." : isEdit ? "Update" : "Create"}
          </button>
          <Link
            href="/admin/testimonials"
            className="px-6 py-3 rounded-xl border border-white/10 text-white/50 hover:text-white hover:border-white/20 transition-colors"
          >
            Cancel
          </Link>
        </div>
      </form>
    </div>
  );
}
